import React, { FunctionComponent } from 'react';
import { withFocusable } from '../common/Focusable';
import { generateDataAttr } from '../utils/generateDataAttr';
import { SKIN, TYPE, SIZE } from './Badge.constants';
import { st, classes } from './Badge.st.css';
import { Text } from '../Text/Text';
import { BadgeProps } from './Badge.types';

type BadgePropsWithFocusable = BadgeProps & {
  focusableOnFocus?: () => void;
  focusableOnBlur?: () => void;
} & {
  'data-is-inverted'?: string;
};

const getProps = (props: BadgePropsWithFocusable) => {
  // that's what you pay for using HOCs...
  const { focusableOnFocus, focusableOnBlur, ...rest } = props;
  return rest;
};

const getFocusableProps = (props: BadgePropsWithFocusable) => {
  // add focusable hooks only when badge is clickable
  const { onClick, focusableOnFocus, focusableOnBlur } = props;
  return onClick
    ? {
        onFocus: focusableOnFocus,
        onBlur: focusableOnBlur,
        tabIndex: 0,
      }
    : {};
};

function Badge(props: BadgeProps) {
  const {
    type = TYPE.solid,
    skin = SKIN.general,
    size = SIZE.medium,
    uppercase = true,
    noPadding = false,
    showTooltip = true,
    display = 'inline',
    children,
    customContent,
    prefixIcon,
    suffixIcon,
    onClick,
    dataHook,
    className,
    ariaLabel,
    ...rest
  } = getProps(props);

  return (
    <div
      aria-label={ariaLabel}
      role={onClick ? 'button' : undefined}
      data-hook={dataHook}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (event: React.KeyboardEvent<HTMLDivElement>) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick(event);
              }
            }
          : undefined
      }
      {...getFocusableProps(props)}
      className={st(
        classes.root,
        {
          clickable: !!onClick,
          display,
          type,
          skin,
          size,
          uppercase,
          noPadding,
          ...rest,
        },
        className,
      )}
      {...generateDataAttr({ type, skin, size, uppercase }, [
        'type',
        'skin',
        'size',
        'uppercase',
      ])}
      data-clickable={!!onClick}
      /* TODO: this prop used to come from stylable v1 spread and is used for testing. Update component testkit to use stylable testkit instead */
      data-is-inverted={rest['data-is-inverted']}
    >
      {prefixIcon &&
        React.cloneElement(prefixIcon, {
          className: classes.prefix,
          'data-prefix-icon': true,
        })}

      {customContent ? (
        customContent
      ) : (
        <Text
          className={classes.text}
          ellipsis
          size={size === 'medium' ? 'small' : size}
          weight="normal"
          showTooltip={showTooltip}
        >
          {children}
        </Text>
      )}

      {suffixIcon &&
        React.cloneElement(suffixIcon, {
          className: classes.suffix,
          'data-suffix-icon': true,
        })}
    </div>
  );
}

Badge.displayName = 'Badge';

const WrappedBadge = withFocusable(
  Badge as FunctionComponent<BadgeProps>,
) as FunctionComponent<BadgeProps>;

export { WrappedBadge as Badge };
